import * as d3 from 'd3';
import { Selection, BaseType, Pie, Arc } from 'd3';
import { toggleClassByWidth } from '../../js/utils/d3-helpers.ts';

// D3セレクションの型エイリアスを定義
type D3Selection<T extends BaseType> = Selection<T, unknown, null, undefined>;

/**
 * PFCバランス円グラフ (PfcPieChart)
 *
 * 状態Aから状態Bへの補間アニメーションを requestAnimationFrame を使って実装。
 * 新仕様 v2.0準拠: DOM操作は原則として data-chart-role 属性を使用する。
 */
export class PfcPieChart {
	// --- 静的プロパティ ---
	static ITEM_STYLES = {
		"protein": { color: "#e15759", legendLabel: "P", text: "タンパク質" },
		"fat": { color: "#f28e2b", legendLabel: "F", text: "脂質" },
		"carb": { color: "#4e79a7", legendLabel: "C", text: "炭水化物" }
	};
	static defaultConfig = {
		responsiveBreakpoint: 480,
		animationDuration: 750,
	};

	// --- プロパティ定義 ---
	private chartElement: HTMLElement;
	private config: {
		responsiveBreakpoint: number;
		animationDuration: number;
		[key: string]: any;
	};
	private svg!: D3Selection<SVGSVGElement>;
	private chartGroup!: D3Selection<SVGGElement>;
	private legendGroup!: D3Selection<SVGGElement>;
	private subValuesGroup!: D3Selection<SVGGElement>;
	private tooltip!: D3Selection<HTMLDivElement>;

	private allChartData: PfcPieChartData | null = null;
	private currentDatasetKey: string | null;
	private currentPieData: PfcPieArcDatum[] = [];
	public isReady: boolean = false;

	private animationState = {
		startTime: 0,
		fromPieData: [] as PfcPieArcDatum[],
		toPieData: [] as PfcPieArcDatum[],
		isActive: false,
		animationFrameId: null as number | null
	};

	private onAnimationStart: (() => void) | null = null;
	private onAnimationEnd: (() => void) | null = null;

	private pie: Pie<this, PfcChartDataItem>;
	private arc: Arc<any, PfcPieArcDatum>;

	private margin: { top: number, right: number, bottom: number, left: number };
	private width: number = 0;
	private height: number = 0;
	private radius: number = 0;

	constructor(chartElement: HTMLElement, config: object = {}) {
		this.chartElement = chartElement;
		this.config = { ...PfcPieChart.defaultConfig, ...config };
		this.currentDatasetKey = this.chartElement.dataset.initialKey || null;

		this.margin = { top: 20, right: 120, bottom: 20, left: 20 };
		this.pie = d3.pie<this, PfcChartDataItem>().value(d => d.value).sort(null);
		this.arc = d3.arc<PfcPieArcDatum>();
	}

	/**
	 * アニメーションイベントのコールバックを登録する
	 * @param eventName - 'animationStart' または 'animationEnd'
	 * @param callback - 実行するコールバック関数
	 */
	public on(eventName: 'animationStart' | 'animationEnd', callback: () => void): void {
		if (eventName === 'animationStart') {
			this.onAnimationStart = callback;
		} else if (eventName === 'animationEnd') {
			this.onAnimationEnd = callback;
		}
	}

	public async init(): Promise<void> {
		const jsonUrl = this.chartElement.dataset.jsonUrl;
		if (!jsonUrl || !this.currentDatasetKey) {
			this._displayMessage('必須のデータ属性が不足しています。', 'error');
			return;
		}

		try {
			const allJsonData = await d3.json<{[key: string]: PfcDataItem[]}>(jsonUrl) ?? null;
			if (allJsonData) {
				this.allChartData = this._processAllData(allJsonData);
			}
		} catch (error) {
			this._displayMessage('データファイルの読み込みに失敗しました。', 'error');
			console.error("Failed to load or parse chart data:", error);
			throw error;
		}
		
		const initialDataset = this.allChartData?.[this.currentDatasetKey];
		if (!initialDataset) {
			this._displayMessage(`初期データ "${this.currentDatasetKey}" が見つかりません。`, 'error');
			return;
		}

		// 【修正】ローディングメッセージを Role属性 で取得して非表示
		const loadingMessage = this.chartElement.querySelector<HTMLElement>('[data-chart-role="loading"]');
		if (loadingMessage) loadingMessage.style.display = 'none';

		this._createTooltip();
		this._setupLayers();
		this._setupDimensions();
		
		this.currentPieData = this.pie(initialDataset);
		this.drawFrame(this.currentPieData);

		this.isReady = true;
	}
	
	public updateByKey(key: string): void {
		if (!this.isReady || !this.allChartData?.[key] || this.animationState.isActive) {
			return;
		}
		
		if (this.onAnimationStart) {
			this.onAnimationStart();
		}
		
		this.currentDatasetKey = key;
		
		const fromData = this.currentPieData;
		const toDataRaw = this.allChartData[key];
		const toData = this.pie(toDataRaw);
		
		const matchedFromData = toData.map(targetItem => {
			const sourceItem = fromData.find(d => d.data.name === targetItem.data.name);
			return sourceItem || { ...targetItem, startAngle: targetItem.endAngle, endAngle: targetItem.endAngle, value: 0 };
		});
		
		this.animationState = {
			...this.animationState,
			fromPieData: matchedFromData,
			toPieData: toData,
			isActive: true,
		};
		
		this._startAnimationLoop();
	}

	public redrawOnResize(): void {
		if (!this.isReady) return;
		this._setupDimensions();
		this.drawFrame(this.currentPieData);
	}
	
	private _startAnimationLoop(): void {
		if (this.animationState.animationFrameId) {
			cancelAnimationFrame(this.animationState.animationFrameId);
		}
		this.animationState.startTime = performance.now();
		this.animationState.animationFrameId = requestAnimationFrame(() => this._animationStep());
	}
	
	private _animationStep(): void {
		const elapsedTime = performance.now() - this.animationState.startTime;
		let progress = elapsedTime / this.config.animationDuration;
		if (progress >= 1) progress = 1;

		const interpolator = d3.interpolate<PfcPieArcDatum[]>(this.animationState.fromPieData, this.animationState.toPieData);
		const interimData = interpolator(progress);
		
		this.drawFrame(interimData);

		if (progress < 1) {
			this.animationState.animationFrameId = requestAnimationFrame(() => this._animationStep());
		} else {
			this.animationState.isActive = false;
			this.animationState.animationFrameId = null;
			this.currentPieData = this.animationState.toPieData;

			if (this.onAnimationEnd) {
				this.onAnimationEnd();
			}
		}
	}
	
	private drawFrame(pieData: PfcPieArcDatum[]): void {
		const isNarrow = this.svg.classed('is-narrow');
		const dataset = pieData.map(d => d.data);
		
		this._drawArcs(pieData);
		this._drawLegend(dataset);

		if (isNarrow) {
			this.chartGroup.selectAll("g.center-text-group").remove();
			this._drawSubValues(dataset);
		} else {
			this.subValuesGroup.selectAll("*").remove();
			this._drawCenterText(dataset);
		}
	}

	private _drawArcs(pieData: PfcPieArcDatum[]): void {
		this.arc.outerRadius(this.radius * 0.9).innerRadius(this.radius * 0.6);

		this.chartGroup.selectAll<SVGPathElement, PfcPieArcDatum>("path.segment")
			.data(pieData, d => d.data.name)
			.join(
				enter => enter.append("path")
					.attr("class", "segment")
					.attr("fill", d => d.data.color)
					.on("mouseover", (event, d) => this.handleArcMouseOver(event, d, pieData))
					.on("mousemove", (event) => this.handleArcMouseMove(event))
					.on("mouseout", () => this.handleArcMouseOut()),
				update => update,
				exit => exit.remove()
			)
			.attr("d", this.arc);
	}
	
	private handleArcMouseOver(event: MouseEvent, d: PfcPieArcDatum, pieData: PfcPieArcDatum[]): void {
		d3.select(event.currentTarget as SVGPathElement).classed("is-hovered", true);
		this.tooltip.classed("is-visible", true);

		const totalValue = d3.sum(pieData, item => item.data.value);
		let tooltipHtml = '';
		if (totalValue > 0) {
			const percent = (d.data.value / totalValue * 100).toFixed(1);
			tooltipHtml = `<div class="tooltip-main-text"><strong>${d.data.text}: ${percent}%</strong></div>`;
			if (d.data.kcal !== undefined) {
				tooltipHtml += `<div class="tooltip-sub-text">(${d.data.value.toFixed(1)}g / ${d.data.kcal.toLocaleString()}kcal)</div>`;
			} else {
				tooltipHtml += `<div class="tooltip-sub-text">(${d.data.value.toFixed(1)}g)</div>`;
			}
		}
		if (tooltipHtml) {
			this.tooltip.html(tooltipHtml);
		}
	}

	private handleArcMouseMove(event: MouseEvent): void {
		this.tooltip.style("left", `${event.pageX + 15}px`).style("top", `${event.pageY - 28}px`);
	}

	private handleArcMouseOut(): void {
		this.chartGroup.selectAll("path.segment").classed("is-hovered", false);
		this.tooltip.classed("is-visible", false);
	}

	private _drawLegend(dataset: PfcChartDataItem[]): void {
		const isNarrow = this.svg.classed('is-narrow');
		const legendItemSize = 18;
		const legendSpacing = 8;
		
		const legendItems = this.legendGroup.selectAll<SVGGElement, PfcChartDataItem>(".legend-item")
			.data(dataset, d => d.name)
			.join(
				enter => {
					const g = enter.append("g").attr("class", "legend-item");
					g.append("rect").attr("width", legendItemSize).attr("height", legendItemSize);
					g.append("text")
						.attr("y", legendItemSize / 2)
						.attr("dy", "0.35em");
					return g;
				}
			);

		legendItems.select("rect").style("fill", d => d.color);
		legendItems.select("text").text(d => d.text);
		
		if (isNarrow) {
			const legendBottomPadding = 25;
			const itemWidths: number[] = [];
			legendItems.each(function() {
				const textNode = d3.select(this).select<SVGTextElement>("text").node();
				if (textNode) {
					itemWidths.push(textNode.getBBox().width + legendItemSize + legendSpacing);
				}
			});
			const totalLegendWidth = d3.sum(itemWidths) + legendSpacing * (dataset.length - 1);
			let currentX = 0;
			legendItems.attr("transform", (d, i) => {
				const x = currentX;
				currentX += itemWidths[i] + legendSpacing;
				return `translate(${x}, 0)`;
			});
			legendItems.select("rect").attr("x", 0);
			legendItems.select("text").attr("x", legendItemSize + legendSpacing);
			const legendX = (this.width + this.margin.left + this.margin.right - totalLegendWidth) / 2;
			const legendY = this.margin.top + this.height + legendBottomPadding;
			this.legendGroup.attr("transform", `translate(${legendX}, ${legendY})`);
		} else {
			const legendItemHeight = 25;
			legendItems.attr("transform", (d, i) => `translate(0, ${i * legendItemHeight})`);
			legendItems.select("rect").attr("x", 0);
			legendItems.select("text").attr("x", legendItemSize + legendSpacing);
			const legendX = this.margin.left + this.width + 40;
			const legendY = (this.height - (dataset.length * legendItemHeight)) / 2 + this.margin.top;
			this.legendGroup.attr("transform", `translate(${legendX}, ${legendY})`);
		}
	}

	private _drawCenterText(dataset: PfcChartDataItem[]): void {
		const unit = 'g';
		const lineHeight = 28;
		const labelX = -this.radius * 0.35;
		const valueX = this.radius * 0.35;
		
		const centerTextGroups = this.chartGroup.selectAll<SVGGElement, PfcChartDataItem>("g.center-text-group")
			.data(dataset, d => d.name)
			.join(
				enter => {
					const g = enter.append("g").attr("class", "center-text-group");
					g.append("text").attr("class", "center-value-label").attr("x", labelX);
					g.append("text").attr("class", "center-value-amount").attr("x", valueX);
					return g;
				}
			)
			.attr("transform", (d, i) => `translate(0, ${(i - 1) * lineHeight})`);
			
		centerTextGroups.select(".center-value-label")
			.style("fill", d => d.color)
			.text(d => `${d.legendLabel}:`);
			
		centerTextGroups.select(".center-value-amount")
			.style("fill", d => d.color)
			.text(d => `${d.value.toFixed(1)} ${unit}`);
	}
	
	private _drawSubValues(dataset: PfcChartDataItem[]): void {
		const unit = 'g';
		const itemSpacing = 15;

		const valueItems = this.subValuesGroup.selectAll<SVGGElement, PfcChartDataItem>("g.sub-value-group")
			.data(dataset, d => d.name)
			.join(
				enter => {
					const g = enter.append("g").attr("class", "sub-value-group");
					g.append("text").attr("class", "sub-value-item");
					return g;
				}
			);

		valueItems.select(".sub-value-item")
			.style("fill", d => d.color)
			.text(d => `${d.legendLabel}: ${d.value.toFixed(1)} ${unit}`);

		const itemWidths: number[] = [];
		valueItems.each(function() { 
			const node = d3.select(this).select<SVGTextElement>('text').node();
			if (node) itemWidths.push(node.getBBox().width);
		});
		const totalWidth = d3.sum(itemWidths) + itemSpacing * (dataset.length - 1);
		let currentX = (this.width + this.margin.left + this.margin.right - totalWidth) / 2;

		valueItems.attr("transform", (d, i) => {
			const x = currentX;
			currentX += itemWidths[i] + itemSpacing;
			return `translate(${x}, 0)`;
		});

		const subValueY = this.margin.top + this.height + 70;
		this.subValuesGroup.attr("transform", `translate(0, ${subValueY})`);
	}
	
	private _setupLayers(): void {
		d3.select(this.chartElement).select("svg").remove();
		this.svg = d3.select(this.chartElement).append("svg");
		this.chartGroup = this.svg.append("g").attr("class", "pie-segments");
		this.legendGroup = this.svg.append("g").attr("class", "legend");
		this.subValuesGroup = this.svg.append("g").attr("class", "sub-values");
	}

	private _setupDimensions(): void {
		const availableWidth = this.chartElement.clientWidth || 400;
		const availableHeight = 400;

		toggleClassByWidth(this.svg, availableWidth, PfcPieChart.defaultConfig.responsiveBreakpoint, 'is-narrow');

		const isNarrow = this.svg.classed('is-narrow');
		this.margin = isNarrow
			? { top: 20, right: 20, bottom: 100, left: 20 }
			: { top: 20, right: 120, bottom: 20, left: 20 };
		
		this.width = availableWidth - this.margin.left - this.margin.right;
		this.height = availableHeight - this.margin.top - this.margin.bottom;
		this.radius = Math.min(this.width, this.height) / 2;

		this.svg.attr("width", this.width + this.margin.left + this.margin.right)
			.attr("height", this.height + this.margin.top + this.margin.bottom);
		this.chartGroup.attr("transform", `translate(${this.margin.left + this.width / 2}, ${this.margin.top + this.height / 2})`);
	}

	private _processAllData(allJsonData: {[key: string]: PfcDataItem[]}): PfcPieChartData {
		const processedData: PfcPieChartData = {};
		for (const key in allJsonData) {
			if (Object.prototype.hasOwnProperty.call(allJsonData, key)) {
				const dataset = allJsonData[key];
				processedData[key] = dataset.map(item => {
					const style = PfcPieChart.ITEM_STYLES[item.name];
					return { ...item, ...style };
				});
			}
		}
		return processedData;
	}

	private _createTooltip(): void {
		const existingTooltip = d3.select<HTMLBodyElement, unknown>("body")
			.select<HTMLDivElement>(".pfc-chart-tooltip");

		if (existingTooltip.empty()) {
			d3.select("body").append("div").attr("class", "pfc-chart-tooltip");
		}
		
		this.tooltip = d3.select<HTMLDivElement, unknown>(".pfc-chart-tooltip") as unknown as D3Selection<HTMLDivElement>;
	}
	
	private _displayMessage(message: string, type: 'error' | 'warning' = 'error'): void {
		// 【修正】Role属性を使って古いメッセージを削除
		d3.select(this.chartElement).select('[data-chart-role="message"]').remove();
		// 【修正】新規メッセージにもRole属性を付与
		d3.select(this.chartElement).append('p')
			.attr('class', `chart-message ${type}`)
			.attr('data-chart-role', 'message')
			.text(message);
	}
}